// src/components/Admin/Auth/LoginForm/LoginForm.js
import React, { useState } from "react";
import { Form, Dimmer, Loader } from "semantic-ui-react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Auth, User } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { initialValues, validationSchema } from "./LoginForm.form";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate, Link } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import "./LoginForm.scss";

const authController = new Auth();
const userController = new User();

const EDUCA360_ROLES = new Set([
  "admin",
  "partner",
  "institution",
  "teacher",
  "student",
  "user",
]);
const REVI_URL = "https://educa360revi.web.app";

function decodeTokenRole(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.role;
  } catch {
    return null;
  }
}

export function LoginForm({ onAuthSuccess }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  // Opt-in de marketing para el alta con Google (desmarcado por defecto,
  // RGPD). Si la cuenta ya existia, el backend solo puede subirlo a true.
  const [marketingConsent, setMarketingConsent] = useState(false);
  const { t } = useTranslation(["auth", "common"]);

  const getPortalPath = (user) => {
    // Fase 1A M4 - K12 Free Refactor: el teacher free entra a las
    // mismas paginas que el teacher pagado.
    const { role } = user;
    if (role === "teacher") {
      return "teacher/salas";
    }
    return "";
  };

  const checkRoleAllowed = (token, vertical) => {
    const role = decodeTokenRole(token);
    // Comprador K12 recien pagado cuyo provisioning aun no ha terminado: no
    // es REVI, su cuenta se esta activando. Mensaje claro en vez del guard.
    if (role === "pending_paid") {
      authController.removeTokens();
      toast.info(
        "Tu cuenta se está activando tras el pago. Podrás entrar en unos minutos; si tarda, escríbenos a soporte@educa360.com",
        { position: "top-center", autoClose: 8000 },
      );
      return false;
    }
    // Rol fuera del set Educa360 → claramente REVI (therapist/patient/revi)
    if (role && !EDUCA360_ROLES.has(role)) {
      authController.removeTokens();
      toast.warning(`Esta cuenta pertenece a REVI. Accede desde ${REVI_URL}`, {
        position: "top-center",
        autoClose: 6000,
      });
      return false;
    }
    // Caso ambiguo: role==="institution" puede ser K12 o REVI legacy. El
    // backend nos da la vertical real mirando a que coleccion apunta.
    if (role === "institution" && vertical === "revi") {
      authController.removeTokens();
      toast.warning(`Esta cuenta pertenece a REVI. Accede desde ${REVI_URL}`, {
        position: "top-center",
        autoClose: 6000,
      });
      return false;
    }
    return true;
  };

  const handleGoogleSuccess = async ({ credential }) => {
    try {
      setLoading(true);
      const result = await authController.loginWithGoogle(credential, {
        emailMarketingConsent: marketingConsent === true,
      });
      const data = result.data ?? result;
      const {
        accessToken,
        refreshToken,
        user: userData,
        isNewUser,
        vertical,
      } = data;

      if (!accessToken || !refreshToken || !userData) {
        throw new Error(t("errors.googleLoginInvalid"));
      }

      if (!checkRoleAllowed(accessToken, vertical)) {
        setLoading(false);
        return;
      }

      authController.setAccessToken(accessToken);
      authController.setRefreshToken(refreshToken);

      // Transicion de salida (portal) antes de entrar, si esta activa.
      if (onAuthSuccess) {
        setLoading(false);
        await onAuthSuccess();
      }

      await login(accessToken, userData);

      // Fase 1A M5 - K12 Free: alta nueva via Google -> onboarding /welcome
      const portalPath = isNewUser ? "portal/welcome" : getPortalPath(userData);
      navigate(`/${portalPath}`, { replace: true });
      setTimeout(() => window.location.reload(), 500);
    } catch (error) {
      setLoading(false);
      toast.error(error.message, {
        position: "top-center",
        autoClose: false,
      });
    }
  };

  const formik = useFormik({
    initialValues: initialValues(),
    validationSchema: validationSchema(t),
    validateOnChange: false,
    onSubmit: async (formValues) => {
      try {
        setLoading(true);
        const result = await authController.login(formValues);
        const data = result.data ?? result;
        const accessToken = data.access;
        const refreshToken = data.refresh;
        const vertical = data.vertical;

        if (!accessToken || !refreshToken) {
          throw new Error(t("errors.loginInvalidResponse"));
        }

        if (!checkRoleAllowed(accessToken, vertical)) {
          setLoading(false);
          return;
        }

        authController.setAccessToken(accessToken);
        authController.setRefreshToken(refreshToken);

        const userData = await userController.getMe(accessToken);

        // Transicion de salida (portal) antes de entrar, si esta activa.
        if (onAuthSuccess) {
          setLoading(false);
          await onAuthSuccess();
        }

        await login(accessToken, userData);

        const portalPath = getPortalPath(userData);
        navigate(`/${portalPath}`, { replace: true });
      } catch (error) {
        setLoading(false);
        toast.error(error.message || t("errors.loginFailed"), {
          position: "top-center",
          autoClose: false,
        });
      }
    },
  });

  return (
    <>
      <ToastContainer />
      {loading && (
        <Dimmer active inverted page>
          <Loader content={t("common:actions.loading")} />
        </Dimmer>
      )}
      <Form onSubmit={formik.handleSubmit}>
        <Form.Input
          type="text"
          icon="user"
          iconPosition="left"
          name="email"
          placeholder={t("login.emailPlaceholder")}
          onChange={formik.handleChange}
          value={formik.values.email}
          error={formik.errors.email}
        />
        <Form.Input
          type="password"
          icon="lock"
          iconPosition="left"
          name="password"
          placeholder={t("login.passwordPlaceholder")}
          onChange={formik.handleChange}
          value={formik.values.password}
          error={formik.errors.password}
        />
        <Form.Button type="submit" primary fluid loading={formik.isSubmitting}>
          {t("login.submitButton")}
        </Form.Button>
        {/* Enlace para recuperar contraseña */}
        <div className="forgot-link">
          <Link to="/recuperar-contraseña" className="link-btn">
            {t("login.forgotPassword")}
          </Link>
        </div>
        {/* Separador "o" */}
        <div className="or-separator">
          <span>{t("login.orSeparator")}</span>
        </div>
        <div className="divider-text">{t("login.loginWith")}</div>
        {/* Google Login dentro del form */}
        <div className="google-area">
          <Form.Checkbox
            name="googleMarketingConsent"
            label={t("login.marketingCheckbox")}
            onChange={(e, data) => setMarketingConsent(Boolean(data.checked))}
            checked={marketingConsent}
          />
          {/* Aviso soft opt-in (LSSI 21.2): correos del servicio con baja
              1-click; la casilla solo cubre promos/newsletter. */}
          <p style={{ fontSize: 12, color: "#8a94a6", margin: "4px 0 10px" }}>
            {t("login.serviceEmailsNotice")}
          </p>
          <div className="google-login-btn">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => {
                setLoading(false);
                toast.error(t("login.googleError"));
              }}
            />
          </div>
        </div>
      </Form>
    </>
  );
}
